import { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { api } from '@/lib/api';
import { useAuth } from '@/contexts/AuthContext';

interface Model {
  id: string;
  legalName: string;
  stageNames: string[];
  email: string;
  verificationStatus: string;
  connectionStatus?: string | null;
  connectedAt?: string;
}

// Simple email validation
function isValidEmail(email: string): boolean {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
}

export default function Models() {
  const [searchInput, setSearchInput] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [isSearching, setIsSearching] = useState(false);
  const queryClient = useQueryClient();
  const { user } = useAuth();

  const isStudio = user?.role === 'studio_admin' || user?.role === 'studio_staff';

  // Auto-search when valid email is entered
  useEffect(() => {
    if (isValidEmail(searchInput)) {
      setSearchQuery(searchInput);
      setIsSearching(true);
    }
  }, [searchInput]);

  // Debounce search for non-email queries (500ms delay)
  useEffect(() => {
    if (!isValidEmail(searchInput) && searchInput.length >= 2) {
      const timer = setTimeout(() => {
        setSearchQuery(searchInput);
        if (searchInput.length >= 2) {
          setIsSearching(true);
        }
      }, 500);
      return () => clearTimeout(timer);
    } else if (searchInput.length < 2) {
      setSearchQuery('');
      setIsSearching(false);
    }
  }, [searchInput]);

  // Fetch connected models (default view for studios)
  const { data: connectedData, isLoading: connectedLoading } = useQuery({
    queryKey: ['models', 'connected'],
    queryFn: () => api.get<{ models: Model[] }>('/models'),
    enabled: !isSearching,
  });

  // Fetch search results
  const { data: searchData, isLoading: searchLoading } = useQuery({
    queryKey: ['models', 'search', searchQuery],
    queryFn: () => api.get<{ models: Model[] }>(`/models/search?q=${encodeURIComponent(searchQuery)}`),
    enabled: isSearching && searchQuery.length >= 2,
  });

  // Request connection mutation
  const requestConnectionMutation = useMutation({
    mutationFn: (modelId: string) => api.post(`/models/${modelId}/request-connection`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['models'] });
    },
  });

  const models = isSearching ? (searchData?.data.models || []) : (connectedData?.data.models || []);
  const isLoading = isSearching ? searchLoading : connectedLoading;

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    if (searchInput.length >= 2) {
      setSearchQuery(searchInput);
      setIsSearching(true);
    }
  };

  const clearSearch = () => {
    setSearchInput('');
    setSearchQuery('');
    setIsSearching(false);
  };

  const getConnectionButton = (model: Model) => {
    if (!isStudio) return null;

    if (model.connectionStatus === 'active') {
      return <span className="badge badge-success">Connected</span>;
    }
    if (model.connectionStatus === 'pending') {
      return <span className="badge badge-warning">Pending</span>;
    }
    if (model.connectionStatus === 'rejected') {
      return (
        <button
          onClick={() => requestConnectionMutation.mutate(model.id)}
          className="btn btn-small btn-secondary"
          disabled={requestConnectionMutation.isPending}
        >
          Request Again
        </button>
      );
    }
    return (
      <button
        onClick={() => requestConnectionMutation.mutate(model.id)}
        className="btn btn-small btn-primary"
        disabled={requestConnectionMutation.isPending}
      >
        Request Connection
      </button>
    );
  };

  return (
    <div className="page">
      <div className="page-header">
        <h1>Models</h1>
        <Link to="/models/new" className="btn btn-primary">
          Add Model
        </Link>
      </div>

      <div className="filters">
        <form onSubmit={handleSearch} className="search-form">
          <input
            type="text"
            placeholder="Search by name, stage name, email, or phone..."
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            className="search-input"
          />
          {searchInput && (
            <button type="button" onClick={clearSearch} className="btn btn-small btn-secondary">
              Clear
            </button>
          )}
          <button type="submit" className="btn btn-small btn-primary" disabled={searchInput.length < 2}>
            Search
          </button>
        </form>
      </div>

      {isSearching && (
        <div className="search-info">
          <p>
            Showing search results for "{searchQuery}".{' '}
            <button onClick={clearSearch} className="link">
              Show connected models
            </button>
          </p>
        </div>
      )}

      {!isSearching && isStudio && (
        <div className="info-banner">
          <p>Showing models connected to your studio. Use the search to find and request connections to new models.</p>
        </div>
      )}

      {isLoading ? (
        <div className="loading">Loading models...</div>
      ) : models.length === 0 ? (
        <div className="empty-state">
          {isSearching ? (
            <>
              <p>No models found matching "{searchQuery}"</p>
              <button onClick={clearSearch} className="btn btn-secondary">
                Show Connected Models
              </button>
            </>
          ) : (
            <>
              <p>No connected models yet</p>
              <p className="text-muted">Search for models to request connections</p>
            </>
          )}
        </div>
      ) : (
        <div className="table-container">
          <table className="table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Stage Names</th>
                <th>Email</th>
                <th>Status</th>
                {isStudio && <th>Connection</th>}
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {models.map((model) => (
                <tr key={model.id}>
                  <td>
                    <Link to={`/models/${model.id}`} className="link">
                      {model.legalName}
                    </Link>
                  </td>
                  <td>
                    {Array.isArray(model.stageNames)
                      ? model.stageNames.join(', ')
                      : model.stageNames || '-'}
                  </td>
                  <td>{model.email || '-'}</td>
                  <td>
                    <span
                      className={`badge ${model.verificationStatus === 'verified' ? 'badge-success' : 'badge-warning'}`}
                    >
                      {model.verificationStatus}
                    </span>
                  </td>
                  {isStudio && (
                    <td>{getConnectionButton(model)}</td>
                  )}
                  <td>
                    <Link to={`/models/${model.id}`} className="btn btn-small">
                      View
                    </Link>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
